import { Controller } from "stimulus"

// Thin interaction layer for the safety-net flow. All step transitions, the
// duration→bucket resolution, progress geometry, and persistence now happen
// server-side: each step is a Turbo Frame rendered by SafetyNetsController, and
// the quiz/pledge forms submit normally (Turbo swaps the frame in the response).
//
// This controller only handles in-form interactivity that has no business going
// to the server on every keystroke: highlighting the selected duration option,
// formatting currency inputs, the weekly-amount picker panel, the live pledge
// preview, and enabling/disabling the submit buttons.
export default class extends Controller {
  static get targets() {
    return [
      "durationOption",
      "durationOptionField",
      "customAmountInput",
      "revealButton",
      "weeklyAmountInput",
      "weeklyRecommendedBadge",
      "weeklyPanel",
      "pledgeWeekly",
      "pledgeMonthly",
      "submitPledgeButton",
    ]
  }

  static get values() {
    return {
      recommendedWeekly: Number,
    }
  }

  connect() {
    this._outsideClickHandler = (e) => {
      if (!this.hasWeeklyPanelTarget) return
      const insideInput = this.hasWeeklyAmountInputTarget && this.weeklyAmountInputTarget.contains(e.target)
      const insidePanel = this.weeklyPanelTarget.contains(e.target)
      if (!insideInput && !insidePanel) this._closeWeeklyPanel()
    }
    document.addEventListener("click", this._outsideClickHandler)

    this._updateRevealButtonState()
    this._updateSubmitPledgeButtonState()
    this._updateWeeklyRecommendedBadge()
  }

  disconnect() {
    if (this._outsideClickHandler) document.removeEventListener("click", this._outsideClickHandler)
  }

  // Re-sync button/badge state whenever a step's form enters the DOM via a Turbo
  // Frame swap (connect() only runs once, on the persistent outer element).
  revealButtonTargetConnected() {
    this._updateRevealButtonState()
  }

  weeklyAmountInputTargetConnected() {
    this._updateSubmitPledgeButtonState()
    this._updateWeeklyRecommendedBadge()
  }

  selectDuration(event) {
    const btn = event.currentTarget
    this.durationOptionTargets.forEach(el => this._setOptionSelected(el, el === btn))
    if (this.hasDurationOptionFieldTarget) this.durationOptionFieldTarget.value = btn.dataset.optionKey
    if (this.hasCustomAmountInputTarget) this.customAmountInputTarget.value = ""
    this._updateRevealButtonState()
  }

  clearDurationSelection() {
    this.durationOptionTargets.forEach(el => this._setOptionSelected(el, false))
    if (this.hasDurationOptionFieldTarget) this.durationOptionFieldTarget.value = ""
    this._updateRevealButtonState()
  }

  onCustomAmountInput(event) {
    const input = event.target
    const cleaned = input.value.replace(/[^0-9.]/g, "")
    if (input.value !== cleaned) input.value = cleaned
    if (this.hasDurationOptionFieldTarget) {
      this.durationOptionFieldTarget.value = cleaned.length ? "custom" : ""
    }
    this._updateRevealButtonState()
  }

  formatCustomAmount(event) {
    const input = event.target
    const num = this._parseAmount(input.value)
    input.value = num > 0 ? "$" + num.toLocaleString() : ""
  }

  onWeeklyAmountChange() {
    this._updatePledgeText()
    this._updateSubmitPledgeButtonState()
    this._updateWeeklyRecommendedBadge()
  }

  formatWeeklyAmount(event) {
    const input = event.target
    const num = this._parseAmount(input.value)
    input.value = num > 0 ? "$" + num.toLocaleString() : ""
    this._updateSubmitPledgeButtonState()
    this._updateWeeklyRecommendedBadge()
  }

  openWeeklyPanel() {
    if (!this.hasWeeklyPanelTarget) return
    this.weeklyPanelTarget.classList.remove("tw-opacity-0", "tw-scale-95", "tw-pointer-events-none")
    this.weeklyPanelTarget.classList.add("tw-opacity-100", "tw-scale-100")
  }

  closeWeeklyPanel() {
    this._closeWeeklyPanel()
  }

  pickWeeklyAmount(event) {
    event.preventDefault()
    const amount = Number(event.currentTarget.dataset.weeklyAmount)
    if (!amount || !this.hasWeeklyAmountInputTarget) return
    this.weeklyAmountInputTarget.value = "$" + amount.toLocaleString()
    this._updatePledgeText()
    this._updateSubmitPledgeButtonState()
    this._updateWeeklyRecommendedBadge()
    this._closeWeeklyPanel()
    this.weeklyAmountInputTarget.blur()
  }

  _setOptionSelected(el, selected) {
    el.dataset.selected = selected ? "true" : "false"
    el.classList.toggle("tw-bg-muted-blue", selected)
    el.classList.toggle("tw-bg-gray-lighter", !selected)
    el.classList.toggle("hover:tw-bg-indigo-50", !selected)
    el.style.borderColor = selected ? "#5A50E6" : "#E5E7EB"
    el.style.borderWidth = selected ? "2px" : "1px"
    el.style.borderStyle = "solid"
  }

  _hasSelection() {
    if (!this.hasDurationOptionFieldTarget) return false
    const opt = this.durationOptionFieldTarget.value
    if (!opt) return false
    if (opt === "custom") {
      return this.hasCustomAmountInputTarget && this._parseAmount(this.customAmountInputTarget.value) > 0
    }
    return true
  }

  _updateRevealButtonState() {
    if (!this.hasRevealButtonTarget) return
    const enabled = this._hasSelection()
    this.revealButtonTarget.disabled = !enabled
    this.revealButtonTarget.classList.toggle("tw-opacity-50", !enabled)
    this.revealButtonTarget.classList.toggle("tw-cursor-not-allowed", !enabled)
  }

  _currentWeeklyAmount() {
    if (this.hasWeeklyAmountInputTarget) {
      const v = this._parseAmount(this.weeklyAmountInputTarget.value)
      if (v > 0) return v
    }
    return this.recommendedWeeklyValue
  }

  _updateSubmitPledgeButtonState() {
    if (!this.hasSubmitPledgeButtonTarget) return
    const disabled = !this._currentWeeklyAmount()
    this.submitPledgeButtonTarget.disabled = disabled
    this.submitPledgeButtonTarget.classList.toggle("tw-opacity-50", disabled)
    this.submitPledgeButtonTarget.classList.toggle("tw-cursor-not-allowed", disabled)
  }

  _updateWeeklyRecommendedBadge() {
    if (!this.hasWeeklyRecommendedBadgeTarget || !this.hasWeeklyAmountInputTarget) return
    const current = this._parseAmount(this.weeklyAmountInputTarget.value)
    const matches = current > 0 && current === this.recommendedWeeklyValue
    this.weeklyRecommendedBadgeTarget.hidden = !matches
  }

  _closeWeeklyPanel() {
    if (!this.hasWeeklyPanelTarget) return
    this.weeklyPanelTarget.classList.add("tw-opacity-0", "tw-scale-95", "tw-pointer-events-none")
    this.weeklyPanelTarget.classList.remove("tw-opacity-100", "tw-scale-100")
  }

  _updatePledgeText() {
    const weekly = this._currentWeeklyAmount()
    const monthly = weekly * 4
    this.pledgeWeeklyTargets.forEach(el => { el.textContent = "$" + weekly.toLocaleString() })
    this.pledgeMonthlyTargets.forEach(el => { el.textContent = "$" + monthly.toLocaleString() })
  }

  _parseAmount(val) {
    return Number((val || "").toString().replace(/[^0-9.]/g, "")) || 0
  }
}
